// <InsightVisual> — the single React host that renders any supported visual.
//
// It instantiates insight-bi's vanilla-JS chart class against a real DOM node
// and drives it through the IVisual lifecycle (render / update / destroy) —
// the exact lifecycle the Angular wrapper performed, but native React. The
// chart's BaseVisual constructor takes a *selector string* and does its own
// document.querySelector, so we mount a div with a unique id and hand it
// `#<id>`. Plotly + the chart's vendored source are lazy-loaded on first use.

import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { createPortal } from 'react-dom'

import { NativeVisualMenu } from './NativeVisualMenu'
import {
  detectDimHierarchy, deriveDrillView, drillBreadcrumb, canDrillDeeper, mergeDrillValues,
  dimensionFiltersFromRow, underlyingClientRows,
} from './drilldown'
import type { DrillState } from './drilldown'
import type { SelectInfo } from './drillThrough'
import { quickFilterPicksFromRow } from './quickFilter'
import type { QuickFilterPick } from './quickFilter'
import {
  applyFilterToData, collapseToDisplayedGrain, combineFilters, filterColumns,
  filterKey, hasFilterRules, FILTER_REASON_NOT_IN_DATA,
} from './visualFilter'
import type { FilterExpression, FilterResult } from './visualFilter'
import { VisualFilterDialog } from './VisualFilterDialog'
import { FunnelIcon } from './QuickFilterBar'
import { RowLimitBadge } from './RowLimitBadge'
import type { VisualModel } from './visualModel'
import type { VisualPlan } from './planToReport'
import { ensurePlotly, ensureVendor, resizeChart } from './loader'
import { ensureVizThemeCss, useDarkSurface } from './surfaceTheme'
import { themeVendorOptions } from './plotlyTheme'
import { registerChartHost, unregisterChartHost } from './chartAdvFormat'
import { resolveChartType } from './registry'
import type { NativeKind } from './registry'
import { mapVisualData, pickGroup } from './dataUtils'
import { formatCell, displayToMask } from './formatters'
import { GridTable } from './GridTable'
import { HtmlVisual } from './nativeRenderers'
import { XslVisual } from './XslVisual'
import type { NativeRendererProps, SelectHandler } from './nativeRenderers'
import { CalendarVisual } from './CalendarVisual'
import { CardsVisual } from './CardsVisual'
import { FeedsVisual } from './FeedsVisual'
import { PivotVisual } from './PivotVisual'
import { TreeOrgChart } from './TreeOrgChart'
import { GanttChart } from './GanttChart'
import { ComboChart } from './ComboChart'
import { MapVisual } from './MapVisual'
import type { ComponentType } from 'react'
import type { ChartConstructor, ChartInstance, VisualJsonData, VisualLayout, VisualOption } from './types'
import { registerEventOverlay, type MountHooks } from './events/overlay'
import type { EventItem, EventLayer, EventsBlock } from './events/model'
import { NotesPanel } from './events/NotesPanel'
import { useVisualNotesTransport } from './events/notesContext'

// The vendored charts reach the shared events overlay through a window hook
// (BaseVisual._applyEventOverlay) — register it once, before any chart draws.
registerEventOverlay()

// Native React renderers, keyed by NativeKind (no vendored JS class).
const NATIVE: Record<NativeKind, ComponentType<NativeRendererProps>> = {
  table: GridTable,
  pivot: PivotVisual,
  cards: CardsVisual,
  feeds: FeedsVisual,
  html: HtmlVisual,
  xsl: XslVisual,
  calendar: CalendarVisual,
  tree: TreeOrgChart,
  gantt: GanttChart,
  combo: ComboChart,
  map: MapVisual,
  document: DocumentPlaceholder,
}

/**
 * What a Document visual looks like to a host that draws charts.
 *
 * Its real renderer is the document engine, which carries the TipTap editor —
 * pulling that in here would put the editor in every bundle that draws a bar
 * chart, and the build has a hard ceiling this would breach. So the hosts that
 * CAN render one (the preview pane, the smart-page embed, the runtime client)
 * dispatch before they reach this package, and the ones that cannot — an
 * assistant's inline chart, a card thumbnail — land here and say so.
 *
 * A placeholder rather than an error, because nothing has gone wrong: this is
 * a working visual whose output is a document, being asked for by something
 * that draws charts.
 */
function DocumentPlaceholder({ jsonData }: NativeRendererProps) {
  const rows = Math.max(0, (jsonData?.length ?? 1) - 1)
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', gap: 6, padding: 20, minHeight: 120,
      textAlign: 'center', fontSize: 13,
      color: 'var(--pg-text-secondary, var(--color-text-secondary, #6b7280))',
    }}>
      <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true"
           fill="none" stroke="currentColor" strokeWidth="1.5"
           strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
        <path d="M14 2v6h6M8 13h8M8 17h5" />
      </svg>
      <div>This visual is laid out as a document.</div>
      <div style={{ fontSize: 12 }}>
        {rows > 0
          ? `${rows.toLocaleString()} row${rows === 1 ? '' : 's'} — open it to read it.`
          : 'Open it to read it.'}
      </div>
    </div>
  )
}

let _seq = 0
function nextId(): string {
  _seq += 1
  return `insight-visual-${_seq}`
}

// Default height for a vendored chart (Plotly) in autoHeight mode. Charts have
// no intrinsic content height — the drawing surface must be given one — so a
// card/table can be content-short while a chart still gets a usable canvas.
const DEFAULT_CHART_HEIGHT = 360

// In autoHeight mode a content-sized native visual (e.g. a long table or feed)
// could otherwise grow without bound and make the page enormous. Cap it at one
// standard screen height and let it scroll internally past that. A caller can
// override by passing `style.maxHeight`.
const DEFAULT_MAX_HEIGHT = '100vh'

/** The client-side underlying-data payload handed to `onShowUnderlying`. */
export interface UnderlyingPayload {
  /** `[schemaRow, ...rows]` already filtered to the clicked point. */
  jsonData: VisualJsonData
  /** The visual's layout (for column labels/formatting in the modal). */
  visualLayout: VisualLayout
  /** The equality filters applied (raw values, keyed by column NAME). */
  filters: Record<string, unknown>
}

export interface InsightVisualProps {
  jsonData: VisualJsonData
  visualLayout: VisualLayout
  /**
   * This dataset is the first N rows of a larger result (its query hit the row
   * ceiling). It is then a slice, not the population, so a dashboard filter is
   * DECLINED rather than applied to part of the data — the tile reports itself
   * unfiltered instead of showing a partial total that looks authoritative.
   */
  truncated?: boolean
  /** The row limit applied to this run (what the badge reports). */
  rowLimit?: number | null
  /** The most rows one visual may return here — the bound on raising it. */
  rowCeiling?: number | null
  /** The limit is the author's Top-N rather than the instance ceiling. */
  rowLimitAuthored?: boolean
  /** The author pinned the limit, so a viewer may not raise it. */
  rowLimitFixed?: boolean
  /**
   * A viewer asked to see more rows. The host re-fetches with this limit.
   * Absent = the badge states the situation and offers no way out, which is
   * right on a surface that cannot re-query (a thumbnail, a static export).
   */
  onRowLimit?: (limit: number) => void
  /** Options array (defaults to visualLayout.options). */
  optionsArr?: VisualOption[]
  /** Force a chart type; otherwise resolved from visualLayout.className / ID. */
  chartType?: string
  className?: string
  style?: CSSProperties
  /**
   * Size to content instead of filling the container. Native visuals
   * (cards / table / feeds / pivot / calendar / tree / …) take their natural
   * content height; vendored charts (Plotly) have no intrinsic height so they
   * fall back to DEFAULT_CHART_HEIGHT. Smart-page embeds set this so a card
   * visual stays short and a chart gets a sensible default — instead of every
   * visual being stretched to a fixed container height. Default false keeps the
   * legacy fill-the-container behavior (designer canvas, dashboard tiles).
   */
  autoHeight?: boolean
  /** Called when the chart cannot be loaded or rendered. */
  onError?: (error: Error) => void
  /**
   * Called on a SINGLE click with the clicked row's data — drives dashboard
   * drill-through / cross-filter. In-visual hierarchy drill and native chart
   * drill (e.g. treemap zoom) are consumed first and do NOT fire this.
   */
  onSelect?: SelectHandler
  /**
   * OPT-IN: a TABLE visual's row was clicked (or Enter on a focused row) —
   * the Transaction Wizard's `pick_row`. Only the table renderer reads it;
   * other visuals ignore it, and leaving it unset changes nothing.
   */
  onRowClick?: SelectHandler
  /**
   * Called to open the row-level UNDERLYING data behind the visual. Fired by a
   * DOUBLE-click on a data point (with that point's dimension filters) or from
   * the ⋮ menu's "View underlying data" (empty filters = the whole result set).
   * Kept separate from onSelect so it never competes with single-click drill.
   *
   * The rows are computed ENTIRELY client-side from the dataset already loaded
   * into the visual (no backend call) and handed back in `payload.jsonData`, so
   * the host just renders them in the shared modal.
   */
  onShowUnderlying?: (payload: UnderlyingPayload) => void
  /**
   * Dashboard QUICK FILTER mode. While on, a SINGLE click on any element of
   * this visual reports the clicked dimension(s) through `onQuickFilter` — it
   * does NOT drill the hierarchy, does not fire `onSelect`, and does not wait
   * for a possible double-click (the gesture must feel instant). The mode is
   * always host-driven and off by default: nothing about this visual's normal
   * click behaviour changes until the user explicitly enters the mode.
   */
  quickFilterMode?: boolean
  /**
   * Called on a quick-filter click with one pick per clicked DIMENSION (a
   * stacked bar yields its category AND its series; a table row yields each of
   * its dimension columns). `additive` is true when ctrl/⌘ was held — the
   * multi-select gesture.
   */
  onQuickFilter?: (picks: QuickFilterPick[], opts: { additive: boolean }) => void
  /**
   * A filter EXPRESSION this visual applies to its own loaded data before
   * rendering (see visualFilter.ts). Host-owned — the dashboard's quick-filter
   * mode passes its chips as an expression — and AND-combined with whatever the
   * user set from this visual's own ⋮ "Filter" dialog, so both routes run
   * through one evaluator.
   */
  filter?: FilterExpression | null
  /**
   * Reports what this visual did with `filter` (+ its own): which columns it
   * could evaluate, which it could not (with a reason), and the row counts. Lets
   * a host say "not filtered by Region — this visual has no such column" instead
   * of leaving the user guessing.
   */
  onFilterResult?: (result: FilterResult) => void
  /**
   * Hide the ⋮ menu's "Filter" entry point (the in-visual filter builder). The
   * capability is on by default wherever the visual has a menu.
   */
  disableFilterMenu?: boolean
  /**
   * The shared in-memory MODEL this visual folds itself out of, and the server
   * PLAN describing that fold (dimensions / series / measures + aggregation).
   * Supplied by the dashboard, which builds one model per data source; when
   * both are present and the plan is supported, `jsonData` is not used — the
   * dataset is computed here, so a filter change is a local recompute rather
   * than a query. Absent (or an unsupported plan) keeps the classic path.
   */
  model?: VisualModel | null
  plan?: VisualPlan | null
  /**
   * Optional host header slot (an element in the tile/preview title bar). When
   * supplied AND the visual's `toolbar` option is on, the chart renders its ⋮
   * menu button *into this element* — inline in the header — instead of floating
   * it over the plot where it would overlap the chart heading. The dropdown is
   * anchored to the viewport so the tile's overflow can't clip it. Native
   * renderers (pivot/table/cards/…) ALSO honor this: when the visual's `toolbar`
   * option is on they portal their own ⋮ menu (Export CSV / Fullscreen) into
   * this slot.
   */
  chromeHost?: HTMLElement | null
  /**
   * For visuals whose `toolbar` option is on, render the ⋮ menu inside a slim
   * header strip that InsightVisual owns (instead of floating it over the plot /
   * omitting it for natives). Use on surfaces that DON'T already provide a title
   * header/`chromeHost` (e.g. chrome-light embeds & the runtime). Applies to both
   * charts and native renderers; ignored when a `chromeHost` is supplied or the
   * visual has no toolbar menu.
   */
  menuHeader?: boolean
  /**
   * OPT-IN (the Visual designer): a single click on a date of a time-series
   * chart reports that date instead of drilling / selecting — the designer's
   * "click a date to add an event there". Absent ⇒ clicks behave as always.
   */
  onDatePick?: (info: { date: string; label: string; rect: DOMRect | null }) => void
}

// True when the options enable the ⋮ toolbar menu (the only affordance we dock
// into a header). Mirrors BaseVisual's `toolbar` option + its aliases.
function optionsEnableMenu(options: VisualOption[] | undefined): boolean {
  if (!Array.isArray(options)) return false
  return options.some((o) => {
    const name = String((o as { optionName?: string }).optionName || '').toLowerCase()
    if (!name.includes('toolbar')) return false
    const oo = o as { checked?: unknown; defaultValue?: unknown }
    const v = oo.checked != null ? oo.checked : oo.defaultValue
    return v === true || String(v).toLowerCase() === 'true'
  })
}

// Vendored charts that plot a point PER ROW, so their rows are never merged.
const POINT_PER_ROW = new Set(['ScatterPlot', 'BubbleChart'])

// Reconstruct a source data row from a Plotly click point. We have the clicked
// dimension value (point.x / point.label); match it back to a jsonData row on
// the Rows-group dimension column so drill-through can map its fields. The
// non-cartesian trace families carry the clicked dimension in different point
// fields (treemap label, funnel y, scatterpolar theta, sankey source/target),
// so those are handled per trace type without touching the cartesian path.
function rowFromPoint(
  jsonData: VisualJsonData,
  visualLayout: VisualLayout,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  point: any,
): Record<string, unknown> | null {
  const md = mapVisualData(jsonData, visualLayout)
  const traceType = String(point?.data?.type ?? '').toLowerCase()

  // Sankey points aren't dimension/value shaped: a LINK click carries
  // source/target node objects, a NODE click carries just the node label.
  if (traceType === 'sankey') {
    const srcCol = pickGroup(md, 'source')[0]
    const tgtCol = pickGroup(md, 'target')[0]
    const src = point?.source?.label
    const tgt = point?.target?.label
    if (srcCol && tgtCol && src != null && tgt != null) {
      const hit = md.rows.find(
        (r) => String(r[srcCol.name]) === String(src) && String(r[tgtCol.name]) === String(tgt),
      )
      return hit ?? { [srcCol.name]: src, [tgtCol.name]: tgt }
    }
    const nodeLabel = point?.label
    if (nodeLabel != null) {
      if (srcCol && md.rows.some((r) => String(r[srcCol.name]) === String(nodeLabel))) {
        return { [srcCol.name]: nodeLabel }
      }
      if (tgtCol) return { [tgtCol.name]: nodeLabel }
    }
    return null
  }

  // Multi-level treemap: the clicked node's `id` is the level path joined by
  // TreemapChart.PATH_SEP (''). Split it back into the Label columns so
  // onSelect carries EVERY hierarchy value ({country, state, …}). A non-leaf
  // click (fewer segments than the label depth) is consumed by Plotly's zoom —
  // return null so no drill-through / underlying fires.
  if (traceType === 'treemap') {
    const labelCols = pickGroup(md, 'label')
    if (labelCols.length >= 2) {
      const id = point?.id != null ? String(point.id) : (point?.label != null ? String(point.label) : '')
      const segs = id ? id.split(String.fromCharCode(1)) : []
      if (segs.length < labelCols.length) return null // branch node → zoom only
      const matches = (r: Record<string, unknown>) =>
        labelCols.every((c, i) => {
          const want = segs[i]
          return (
            String(r[c.name]) === want ||
            formatCell(r[c.name], { type: c.type, coltype: c.coltype, format: c.format, pattern: c.pattern }) === want
          )
        })
      const hit = md.rows.find(matches)
      if (hit) return hit
      // No exact row — return the filter carrying each level value (best effort).
      const filter: Record<string, unknown> = {}
      labelCols.forEach((c, i) => { if (segs[i] != null) filter[c.name] = segs[i] })
      return filter
    }
  }

  // The new chart types name their dimension group after the concept
  // (Label / Stage / Category / Axis); classic charts use Rows / Columns.
  const dimCol = pickGroup(md, 'rows', 'columns', 'label', 'stage', 'category', 'axis')[0]
  let dimValue: unknown
  // A horizontal bar chart (orientation 'h') puts the CATEGORY on the y-axis and
  // the measure on x — so the dimension value is point.y, not point.x. Reading
  // point.x here would build a filter on the numeric measure and match no rows
  // (empty "underlying data"). Funnel is always horizontal (dimension on y).
  const isHorizontalBar = traceType === 'bar'
    && String(point?.data?.orientation || '').toLowerCase() === 'h'
  if (traceType === 'scatterpolar') dimValue = point?.theta
  else if (traceType === 'funnel') dimValue = point?.y ?? point?.label
  else if (traceType === 'treemap') dimValue = point?.label
  else if (traceType === 'waterfall') dimValue = point?.x
  else if (isHorizontalBar) dimValue = point?.y ?? point?.label
  else dimValue = point?.x ?? point?.label ?? point?.name
  if (dimCol && dimValue != null) {
    const want = String(dimValue)
    const hits = md.rows.filter(
      (r) =>
        String(r[dimCol.name]) === want ||
        formatCell(r[dimCol.name], { type: dimCol.type, coltype: dimCol.coltype, format: dimCol.format, pattern: dimCol.pattern }) === want,
    )
    // Radar with a Series group: several rows share the clicked axis value —
    // prefer the row whose Series column matches the clicked trace's name.
    if (hits.length > 1) {
      const seriesCol = pickGroup(md, 'series')[0]
      const traceName = point?.data?.name
      if (seriesCol && traceName != null) {
        const bySeries = hits.find((r) => String(r[seriesCol.name]) === String(traceName))
        if (bySeries) return bySeries
      }
    }
    if (hits[0]) return hits[0]
    if (dimCol) return { [dimCol.name]: dimValue }
  }
  // Fallback: positional row by pointNumber/pointIndex.
  const idx = point?.pointNumber ?? point?.pointIndex
  if (typeof idx === 'number' && md.rows[idx]) return md.rows[idx]
  return null
}

// The columns a Plotly click point stands for, by NAME: the measure the clicked
// trace plots (matched by the trace's name, or the only measure there is), the
// series and the category. A dashboard tile with one drill-through per column
// follows the link whose column is among these.
function columnsFromPoint(
  jsonData: VisualJsonData,
  visualLayout: VisualLayout,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  point: any,
): SelectInfo {
  const out: string[] = []
  const headings: Record<string, string> = {}
  const add = (col: { name: string; label?: string } | undefined) => {
    if (!col?.name || out.includes(col.name)) return
    out.push(col.name)
    if (col.label) headings[col.name] = String(col.label)
  }
  try {
    const md = mapVisualData(jsonData, visualLayout)
    const traceType = String(point?.data?.type ?? '').toLowerCase()
    const dims = traceType === 'treemap' || traceType === 'sunburst' || traceType === 'icicle'
      ? pickGroup(md, 'label')
      : pickGroup(md, 'rows', 'columns', 'label', 'stage', 'category', 'axis').slice(0, 1)
    const series = pickGroup(md, 'series')[0]
    const taken = new Set([...dims.map((c) => c.name), series?.name].filter(Boolean) as string[])
    const isMeasure = (c: { name: string; type?: string; coltype?: string }) =>
      !taken.has(c.name)
      && (String(c.coltype || '').toUpperCase() === 'NUMBER' || String(c.type || '').toLowerCase() === 'number')
    const measures: Array<{ name: string; label: string }> = []
    for (const cols of Object.values(md.groups)) {
      for (const c of cols) if (isMeasure(c) && !measures.some((m) => m.name === c.name)) measures.push(c)
    }
    const traceName = point?.data?.name != null ? String(point.data.name).trim().toLowerCase() : ''
    const byTrace = traceName
      ? measures.find((m) => m.label.trim().toLowerCase() === traceName || m.name.toLowerCase() === traceName)
      : undefined
    add(byTrace ?? (measures.length === 1 ? measures[0] : undefined))
    add(series)
    for (const d of dims) add(d)
  } catch {
    /* no columns: the host treats the click as "any column" */
  }
  return out.length ? { columns: out, headings } : {}
}

/** Clone a visualLayout, injecting FORMAT / PATTERN masks synthesized from each
 *  column's number/date `display` when the mask is absent (so vendored charts,
 *  which read masks not `display`, still honour a display-authored format). No
 *  column needs it → the original layout is returned unchanged. */
function augmentChartLayoutFormats(layout: VisualLayout): VisualLayout {
  const groups = layout?.ColumnGroups
  if (!Array.isArray(groups) || !groups.length) return layout
  let changed = false
  const nextGroups = groups.map((gRaw) => {
    const g = gRaw as Record<string, unknown>
    const cols = g.COLUMNS as Array<Record<string, unknown>> | undefined
    if (!Array.isArray(cols) || !cols.length) return g
    let groupChanged = false
    const nextCols = cols.map((c) => {
      const display = c.display as Record<string, unknown> | undefined
      if (!display || (c.FORMAT && c.PATTERN)) return c
      const mask = displayToMask(display)
      const patch: Record<string, unknown> = {}
      if (mask.FORMAT && !c.FORMAT) patch.FORMAT = mask.FORMAT
      if (mask.PATTERN && !c.PATTERN) patch.PATTERN = mask.PATTERN
      if (!Object.keys(patch).length) return c
      groupChanged = true
      return { ...c, ...patch }
    })
    if (!groupChanged) return g
    changed = true
    return { ...g, COLUMNS: nextCols }
  })
  return changed ? ({ ...layout, ColumnGroups: nextGroups } as VisualLayout) : layout
}

export function InsightVisual({
  jsonData,
  visualLayout,
  truncated,
  rowLimit,
  rowCeiling,
  rowLimitAuthored,
  rowLimitFixed,
  onRowLimit,
  optionsArr,
  chartType,
  className,
  style,
  autoHeight,
  onError,
  onSelect,
  onRowClick,
  onShowUnderlying,
  quickFilterMode,
  onQuickFilter,
  filter,
  onFilterResult,
  disableFilterMenu,
  model,
  plan,
  chromeHost,
  menuHeader,
  onDatePick,
}: InsightVisualProps) {
  const hostRef = useRef<HTMLDivElement | null>(null)
  // Wrapper around a native renderer — the ⋮ menu's fullscreen target.
  const nativeWrapRef = useRef<HTMLDivElement | null>(null)
  // Light or dark — asked of the background behind this visual, so it is
  // right on every host (Run view, runtime, a themed smart page, a preview)
  // and follows a theme switch while open. See surfaceTheme.ts.
  const dark = useDarkSurface(() => hostRef.current ?? nativeWrapRef.current)
  useEffect(() => { ensureVizThemeCss() }, [])
  const idRef = useRef<string>(nextId())
  const instanceRef = useRef<ChartInstance | null>(null)
  /** The chart class `instanceRef` was built from — an instance only ever
   *  updates in place for the SAME class; another type (or a native renderer
   *  in between) gets a fresh one. */
  const instanceGlobalRef = useRef<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Slot for the InsightVisual-owned slim header (menuHeader mode). Set via a
  // callback ref so it's available once the header is mounted.
  const [internalSlot, setInternalSlot] = useState<HTMLElement | null>(null)

  // ── Events: viewer notes (visualLayout.events, kind "notes") ──────────
  // The notes a viewer just added/changed replace the payload's until the next
  // payload arrives — the chart redraws with them at once, no re-query.
  const notesTransport = useVisualNotesTransport()
  const [notesOverride, setNotesOverride] = useState<{ key: unknown; items: EventItem[]; canAdd?: boolean; admin?: boolean } | null>(null)
  const [notesOpen, setNotesOpen] = useState<{ rect: DOMRect; date?: string } | null>(null)
  const eventsRaw = (visualLayout as { events?: EventsBlock } | undefined)?.events
  const notesLayer: EventLayer | null = eventsRaw?.layers?.find((l) => l.kind === 'notes') ?? null
  const activeNotes = notesOverride && notesOverride.key === eventsRaw ? notesOverride : null
  const datePickRef = useRef(onDatePick); datePickRef.current = onDatePick
  const eventHooksRef = useRef<MountHooks>({})
  eventHooksRef.current = {
    openNotes: notesLayer ? (rect: DOMRect) => setNotesOpen({ rect }) : undefined,
  }

  // Keep the latest inputs in refs so the (once-attached) Plotly click handler
  // always reads current data even on the in-place .update() path. These point
  // at the RENDERED (derived-while-drilled) view so a click resolves against
  // what's actually on screen.
  const dataRef = useRef(jsonData)
  const layoutRef = useRef(visualLayout)
  const selectRef = useRef<SelectHandler | undefined>(onSelect); selectRef.current = onSelect
  const underlyingRef = useRef<((payload: UnderlyingPayload) => void) | undefined>(onShowUnderlying)
  underlyingRef.current = onShowUnderlying
  // Compute the underlying rows CLIENT-SIDE from the currently-rendered dataset
  // (drill-aware via layoutRef/dataRef) and hand them to the host — no backend.
  // Ref-stable so the once-attached Plotly handler + the chart/native menus all
  // route through it and always read the current data.
  const showUnderlyingRef = useRef<(filters: Record<string, unknown>) => void>(() => {})
  showUnderlyingRef.current = (filters: Record<string, unknown>) => {
    const cb = underlyingRef.current
    if (!cb) return
    const jd = underlyingClientRows(dataRef.current, layoutRef.current, filters)
    cb({ jsonData: jd, visualLayout: layoutRef.current, filters })
  }
  // Pending single-click timer — lets us distinguish a single click (drill /
  // select) from a double click (underlying data) on the same data point.
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clickKeyRef = useRef<string>('')
  // Read by the click handler to decide drill-down vs. leaf emit. Initialised
  // null and kept in sync below (drillState / hierarchy are declared later).
  const drillStateRef = useRef<DrillState | null>(null)
  const hierarchyRef = useRef<ReturnType<typeof detectDimHierarchy>>(null)

  // ── Quick filter mode ───────────────────────────────────────────────────
  // Refs so the once-attached Plotly handler always sees the CURRENT mode.
  const quickModeRef = useRef<boolean>(false)
  quickModeRef.current = Boolean(quickFilterMode && onQuickFilter)
  const quickCbRef = useRef<InsightVisualProps['onQuickFilter']>(onQuickFilter)
  quickCbRef.current = onQuickFilter
  // ctrl/⌘ state of the click in flight. Plotly hands us its own DOM event, but
  // native renderers only pass the row — so the mode also records the modifier
  // from a capture-phase document listener (installed only while the mode is on).
  const additiveRef = useRef<boolean>(false)
  // Emit the clicked point's dimensions as quick-filter picks. Returns true when
  // the click was consumed by the mode (so the caller skips drill / onSelect).
  const fireQuickFilterRef = useRef<(row: Record<string, unknown>, additive?: boolean) => boolean>(() => false)
  fireQuickFilterRef.current = (row, additive) => {
    const cb = quickCbRef.current
    if (!quickModeRef.current || !cb) return false
    const merged = mergeDrillValues(row, drillStateRef.current)
    const picks = quickFilterPicksFromRow(merged, layoutRef.current)
    // A click that yields no filterable dimension (a measure-only point, e.g. a
    // KPI card) is still CONSUMED — silently drilling instead would be exactly
    // the surprise this mode exists to avoid. The host reports the no-op.
    cb(picks, { additive: Boolean(additive ?? additiveRef.current) })
    return true
  }

  useEffect(() => {
    if (!quickFilterMode || !onQuickFilter) return
    const onDown = (e: MouseEvent | PointerEvent) => {
      additiveRef.current = Boolean(e.ctrlKey || e.metaKey)
    }
    document.addEventListener('pointerdown', onDown, true)
    return () => document.removeEventListener('pointerdown', onDown, true)
  }, [quickFilterMode, onQuickFilter])

  const options = optionsArr ?? visualLayout?.options ?? []
  // What the vendored chart classes are handed: on a dark surface their
  // white-page `axisTextColor` default becomes white (plotlyTheme.ts).
  const chartOptions = useMemo(() => themeVendorOptions(options, dark), [options, dark])
  const typeDef = resolveChartType(
    chartType, visualLayout?.className, visualLayout?.ID,
  )

  // ── The visual filters ITSELF ───────────────────────────────────────────
  // One mechanism, two entry points: the host's `filter` expression (the
  // dashboard quick-filter chips) and the expression the user builds in this
  // visual's own ⋮ "Filter" dialog. They AND together and are evaluated by the
  // shared evaluator against the data this visual already loaded — so a chart
  // and a table filter identically, and no surface re-queries.
  const [ownFilter, setOwnFilter] = useState<FilterExpression | null>(null)
  const [filterOpen, setFilterOpen] = useState(false)
  const hostFilterKey = filterKey(filter)
  const ownFilterKey = filterKey(ownFilter)
  const effectiveFilter = useMemo(
    () => combineFilters(filter, ownFilter),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hostFilterKey, ownFilterKey],
  )
  // Model-backed: the visual folds the shared dataset itself, WITH the filter
  // applied inside the fold — so an aggregate is recomputed over the filtered
  // rows (a KPI total really becomes that region's total), not sliced after the
  // fact. Falls back to the server dataset if the fold throws.
  const modelBacked = Boolean(model && plan?.supported)
  const modelData = useMemo(() => {
    if (!model || !plan?.supported) return null
    try {
      return model.compute(plan, effectiveFilter)
    } catch (e) {
      console.error('[insight-visual] model fold failed', e)
      return null
    }
  }, [model, plan, effectiveFilter])

  /**
   * Whether these rows are an arbitrary SLICE — which is what decides if a
   * filter may be applied, and is NOT the same question the badge answers.
   *
   * Two different things both mean "there was more data":
   *
   *   * the query hit the instance ROW CEILING. The rows are then the first N
   *     of a population nobody chose, so filtering them would report a partial
   *     total as if it were the answer. Declined.
   *   * the AUTHOR asked for the top 10 and there were more. Those ten rows
   *     ARE the intended population — that is the whole visual — and filtering
   *     them is perfectly meaningful.
   *
   * Conflating the two made every Top-N tile in every existing dashboard stop
   * responding to the quick filter, which is a regression and not a rule
   * anybody asked for. The badge still shows on both (a viewer should be told
   * there is more behind a Top-N); only the filter distinguishes them.
   */
  const filterDeclined = !!truncated && !rowLimitAuthored

  const filterApp = useMemo(() => {
    if (!modelData || !model) {
      return applyFilterToData(jsonData, visualLayout, effectiveFilter,
                               filterDeclined)
    }
    // The fold already applied the filter — but only for columns the shared
    // MODEL carries. One the model has no column for changed nothing, and must
    // be reported as skipped exactly like the non-model path, or the tile would
    // claim a filter it never applied.
    const columns = new Set(model.columns.map((c) => c.toLowerCase()))
    const applied: string[] = []
    const skipped: Array<{ column: string; reason: string }> = []
    for (const column of filterColumns(effectiveFilter)) {
      if (columns.has(column.toLowerCase())) applied.push(column)
      else skipped.push({ column, reason: FILTER_REASON_NOT_IN_DATA })
    }
    const rows = Math.max(0, (modelData.length ?? 1) - 1)
    return {
      jsonData: modelData, applied, skipped,
      rowsBefore: rows, rowsAfter: rows, regrouped: true,
    }
  }, [modelData, model, jsonData, visualLayout, effectiveFilter, filterDeclined])
  // Everything below renders the FILTERED dataset — drill detection, the chart,
  // the native renderers, the ⋮ menu's CSV export and the underlying-data view
  // all read this, so "what I see" and "what I export/inspect" never diverge.
  const filteredData = filterApp.jsonData
  // What a vendored CHART draws: one row per displayed category, aggregated
  // with each value column's FUNCTION — a freehand query without GROUP BY
  // otherwise draws one mark per record (see collapseToDisplayedGrain).
  // Native renderers keep the rows as loaded (a table shows records; the pivot
  // aggregates for itself), and so does a scatter/bubble, which plots a point
  // per row by design. The shared-model path is already folded.
  const chartData = useMemo(
    () => (typeDef?.global && !modelData && !POINT_PER_ROW.has(typeDef.global)
      ? collapseToDisplayedGrain(filteredData, visualLayout) as VisualJsonData
      : filteredData),
    [typeDef?.global, modelData, filteredData, visualLayout],
  )
  const filterActive = hasFilterRules(effectiveFilter)
  // The in-visual filter builder is available wherever the visual has a menu,
  // unless the host opted out.
  const filterMenuEnabled = !disableFilterMenu

  // Report the outcome to the host (row counts + the columns this visual could
  // not evaluate) whenever it changes — never on every render.
  const filterResultKey = `${hostFilterKey}|${ownFilterKey}|${filterApp.applied.join(',')}|`
    + `${filterApp.skipped.map((sk) => `${sk.column}:${sk.reason}`).join(',')}|`
    + `${filterApp.rowsBefore}-${filterApp.rowsAfter}-${filterApp.regrouped}`
  const reportedKeyRef = useRef<string>('')
  const filterAppRef = useRef(filterApp)
  filterAppRef.current = filterApp
  useEffect(() => {
    if (!onFilterResult) return
    if (reportedKeyRef.current === filterResultKey) return
    reportedKeyRef.current = filterResultKey
    const { applied, skipped, rowsBefore, rowsAfter, regrouped } = filterAppRef.current
    onFilterResult({ applied, skipped, rowsBefore, rowsAfter, regrouped })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterResultKey, onFilterResult])

  // ── Generic in-visual drill-down (Phase 7) ──────────────────────────────
  // Enabled only for hierDrill chart families whose category group has ≥2
  // dimensions. Drilling re-renders the SAME visual one level deeper; at the
  // deepest level onSelect fires as usual (drill-through / underlying).
  const [drillState, setDrillState] = useState<DrillState | null>(null)
  const hierarchy = useMemo(
    () => (typeDef?.hierDrill ? detectDimHierarchy(mapVisualData(chartData, visualLayout)) : null),
    [typeDef?.hierDrill, chartData, visualLayout],
  )
  // Reset the drill when the layout STRUCTURE changes (switching visuals), but
  // NOT on a data change — so an auto-refresh keeps the current drill level.
  const layoutKey = useMemo(
    () => JSON.stringify(visualLayout?.ColumnGroups ?? []) + '|' + String(visualLayout?.className ?? '') + '|' + String(visualLayout?.ID ?? ''),
    [visualLayout],
  )
  const layoutKeyRef = useRef(layoutKey)
  useEffect(() => {
    if (layoutKeyRef.current !== layoutKey) {
      layoutKeyRef.current = layoutKey
      setDrillState(null)
    }
  }, [layoutKey])

  // The jsonData + layout actually rendered: the derived (filtered + re-grouped)
  // view while drilled, else the originals. Memoized so the render effect isn't
  // re-run on every parent render.
  const derived = useMemo(
    () => (drillState
      ? deriveDrillView(chartData, visualLayout, drillState)
      : { jsonData: chartData, visualLayout }),
    [chartData, visualLayout, drillState],
  )
  const renderData = derived.jsonData
  const renderLayout = derived.visualLayout
  // Chart-only layout: vendored charts format axis ticks / tooltips from a
  // column's FORMAT / PATTERN mask (not its `display`). Synthesize those masks
  // from a number/date `display` when the mask is absent so a column authored
  // purely with `display` still formats on the chart. Rich display types stay
  // table-only (they yield no mask). Natives are unaffected — they render off
  // `visualLayout` and consume `display` directly.
  const chartLayout = useMemo(() => {
    const base = augmentChartLayoutFormats(renderLayout)
    const ev = (base as { events?: EventsBlock }).events
    if (!activeNotes || !ev?.layers) return base
    return {
      ...base,
      events: {
        ...ev,
        layers: ev.layers.map((l) => (l.kind === 'notes'
          ? { ...l, items: activeNotes.items, can_add: activeNotes.canAdd ?? l.can_add }
          : l)),
      },
    } as VisualLayout
  }, [renderLayout, activeNotes])
  // Sync the click-handler refs to the current rendered view + drill context.
  dataRef.current = renderData
  layoutRef.current = renderLayout
  drillStateRef.current = drillState
  hierarchyRef.current = hierarchy

  const drilled = Boolean(drillState && drillState.levels.length > 0)
  const drillUp = () => setDrillState((s) => {
    if (!s || s.levels.length === 0) return null
    const levels = s.levels.slice(0, -1)
    return levels.length === 0 ? null : { ...s, levels }
  })

  // Ref-stable hooks handed to the vendored chart instance so its ⋮ menu /
  // funnel button drive the SHARED filter dialog instead of BaseVisual's own
  // legacy popup (see BaseVisual._openFilterPopup) — one filtering mechanism.
  const openFilterRef = useRef<() => void>(() => {})
  openFilterRef.current = () => setFilterOpen(true)

  // Whether to render our own slim ⋮ header: only for chart visuals with the
  // toolbar menu on, when the host didn't already provide a header slot.
  const useInternalHeader = Boolean(
    menuHeader && !chromeHost && typeDef && !typeDef.native && optionsEnableMenu(options),
  )
  // The slot the chart's ⋮ button attaches to: an explicit host slot wins;
  // otherwise our internal header slot (menuHeader mode); else none (legacy
  // floating button).
  const effectiveChromeHost = chromeHost ?? (useInternalHeader ? internalSlot : null)
  // Latest slot, read inside the async build() (which may resolve after this
  // render) so the chart always attaches its ⋮ to the current slot.
  const chromeHostRef = useRef<HTMLElement | null>(effectiveChromeHost)
  chromeHostRef.current = effectiveChromeHost

  // (Re)build / update the chart whenever the inputs change.
  useEffect(() => {
    let cancelled = false

    if (!typeDef) {
      const msg = `Unsupported visual type: ${
        chartType || visualLayout?.className || visualLayout?.ID || 'unknown'
      }`
      setError(msg)
      onError?.(new Error(msg))
      return
    }
    setError(null)

    // A different chart type (or a native renderer) must not reuse the live
    // instance: it would keep drawing the old type — or draw into a host that
    // has since been unmounted, so nothing shows when the type comes back.
    const dropInstance = () => {
      if (!instanceRef.current) return
      unregisterChartHost(hostRef.current)
      try { instanceRef.current.destroy() } catch { /* ignore teardown errors */ }
      try {
        chromeHostRef.current?.querySelectorAll('.bv-chrome').forEach((n) => n.remove())
      } catch { /* ignore */ }
      instanceRef.current = null
      instanceGlobalRef.current = null
    }
    // A native renderer replaces the chart's DOM itself — drop the old
    // instance now. A chart-to-chart change keeps the old one on screen until
    // the new type's code has loaded (below), so the swap never shows a blank.
    if (typeDef.native) dropInstance()

    // Native React renderers (e.g. table) have no JS instance to build.
    if (typeDef.native) return

    // Attach a Plotly click handler (drill-down / drill-through / underlying).
    // Plotly's graph div exposes `.on`; newPlot may resolve async, so retry once
    // shortly after render. Attached when EITHER an onSelect is wired OR the
    // chart can drill (drill-down is internal and needs no onSelect).
    function attachPlotlyClick() {
      // Always attach: even with no select/drill/underlying handler wired the
      // empty-area double-click reset below must work (Plotly's native
      // double-click reset is disabled in _buildPlotlyConfig so it can't race
      // the point double-click → underlying modal).
      const tryAttach = (): boolean => {
        const host = hostRef.current
        if (!host) return false
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const gd = (host.querySelector('.js-plotly-plot') || host.querySelector('div')) as any
        if (!gd || typeof gd.on !== 'function') return false
        try { gd.removeAllListeners?.('plotly_click') } catch { /* ignore */ }

        // SINGLE click: drill one hierarchy level (consuming the click) or emit
        // onSelect for drill-through / cross-filter. Native chart drill (e.g.
        // treemap zoom) already happened in Plotly and isn't reached here.
        const runSingleClick = (row: Record<string, unknown>, clicked?: SelectInfo) => {
          // Quick filter mode owns the single click outright: no hierarchy
          // drill, no onSelect drill-through.
          if (fireQuickFilterRef.current(row)) return
          const hier = hierarchyRef.current
          const state = drillStateRef.current
          if (hier) {
            const effective: DrillState = state ?? { dims: hier.dims, groupKey: hier.groupKey, levels: [] }
            if (canDrillDeeper(effective)) {
              const dim = effective.dims[effective.levels.length]
              const rawVal = row[dim.name]
              const lbl = formatCell(rawVal, { type: dim.type, coltype: dim.coltype, format: dim.format, pattern: dim.pattern })
              setDrillState({
                dims: effective.dims,
                groupKey: effective.groupKey,
                levels: [...effective.levels, {
                  dim: dim.name, value: rawVal,
                  label: lbl != null && lbl !== '' ? String(lbl) : String(rawVal ?? ''),
                }],
              })
              return
            }
          }
          if (selectRef.current) {
            selectRef.current(mergeDrillValues(row, state), clicked?.columns?.length ? clicked : undefined)
          }
        }

        // DOUBLE click: open the row-level UNDERLYING data behind the clicked
        // point (its dimension filters, merged with any parent drill values).
        const runDoubleClick = (row: Record<string, unknown>) => {
          if (!underlyingRef.current) return
          const merged = mergeDrillValues(row, drillStateRef.current)
          showUnderlyingRef.current(dimensionFiltersFromRow(merged, layoutRef.current))
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const pointKey = (pt: any): string =>
          String(pt?.curveNumber ?? '') + ':' +
          String(pt?.pointNumber ?? pt?.pointIndex ?? pt?.id ?? pt?.label ?? pt?.x ?? '')

        // Controlled zoom recovery: Plotly's native double-click reset is off
        // (see BaseVisual._buildPlotlyConfig), so re-add it ONLY for a
        // double-click on empty plot area — a double-click on a data point is
        // the underlying-data gesture and must not also jump the axes.
        // plotly_click stamps __lastPointClickAt; a dblclick right after a
        // point click is the point gesture, anything else restores auto-range.
        if (!gd.__dblResetBound) {
          gd.__dblResetBound = true
          gd.addEventListener('dblclick', () => {
            if (Date.now() - (gd.__lastPointClickAt || 0) < 350) return
            try {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const P = (window as any).Plotly
              if (!P?.relayout) return
              const upd: Record<string, boolean> = { 'xaxis.autorange': true, 'yaxis.autorange': true }
              if (gd.layout?.yaxis2) upd['yaxis2.autorange'] = true
              void P.relayout(gd, upd)
            } catch { /* reset is best-effort */ }
          })
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        gd.on('plotly_click', (ev: any) => {
          const pt = ev?.points?.[0]
          if (!pt) return
          // An event flag's own hover is its whole job — never a drill.
          if (pt?.data?.meta?.twastaEvent) return
          gd.__lastPointClickAt = Date.now()
          if (datePickRef.current) {
            const prow = rowFromPoint(dataRef.current, layoutRef.current, pt)
            const rowsCol = pickGroup(mapVisualData(dataRef.current, layoutRef.current), 'rows')[0]
            const raw = prow && rowsCol ? (prow[rowsCol.name] ?? null) : null
            const date = raw == null ? '' : String(raw).slice(0, 10)
            if (date) {
              const src = ev?.event as MouseEvent | undefined
              const rect = src ? new DOMRect(src.clientX, src.clientY, 1, 1) : null
              datePickRef.current({ date, label: String(pt.x ?? date), rect })
              return
            }
          }
          const row = rowFromPoint(dataRef.current, layoutRef.current, pt)
          if (!row) return
          const clickedCols = columnsFromPoint(dataRef.current, layoutRef.current, pt)
          // Trace families that zoom natively on click (treemap / sunburst /
          // icicle) are handled by their dedicated `plotly_*click` events below,
          // which can cancel the native zoom — so ignore them here to avoid a
          // duplicate action.
          const traceType = String(pt?.data?.type ?? '').toLowerCase()
          if (traceType === 'treemap' || traceType === 'sunburst' || traceType === 'icicle') return
          // Quick filter mode: act on the FIRST click, with the modifier read
          // from Plotly's own DOM event. Deferring to watch for a double-click
          // would make the mode's core gesture feel laggy.
          if (quickModeRef.current) {
            const src = ev?.event as { ctrlKey?: boolean; metaKey?: boolean } | undefined
            const additive = Boolean(src?.ctrlKey || src?.metaKey || additiveRef.current)
            if (clickTimerRef.current) {
              clearTimeout(clickTimerRef.current)
              clickTimerRef.current = null
              clickKeyRef.current = ''
            }
            fireQuickFilterRef.current(row, additive)
            return
          }
          const key = pointKey(pt)
          // Second click on the same point while a first click is pending → it's
          // a double-click: cancel the single-click action and show underlying.
          if (clickTimerRef.current && clickKeyRef.current === key) {
            clearTimeout(clickTimerRef.current)
            clickTimerRef.current = null
            clickKeyRef.current = ''
            runDoubleClick(row)
            return
          }
          // With no underlying handler wired there's nothing to double-click for,
          // so act on the single click immediately (keeps drill snappy).
          if (!underlyingRef.current) { runSingleClick(row, clickedCols); return }
          // Otherwise defer the single-click action briefly to see if a second
          // click follows (double → underlying).
          if (clickTimerRef.current) clearTimeout(clickTimerRef.current)
          clickKeyRef.current = key
          clickTimerRef.current = setTimeout(() => {
            clickTimerRef.current = null
            clickKeyRef.current = ''
            runSingleClick(row, clickedCols)
          }, 260)
        })

        // Native-drill traces (treemap / sunburst / icicle): Plotly would zoom
        // into the clicked node on a single click, and that zoom happens BEFORE
        // a plain plotly_click handler could react — so a double-click used to
        // both zoom AND open underlying. The dedicated `plotly_*click` events
        // fire first and, when the handler returns false, CANCEL the native
        // zoom. We take that over entirely: prevent the native zoom always, then
        // replicate it ourselves on a single click (after the double-click
        // window) and open underlying on a double click (no zoom).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const onNativeDrillClick = (ev: any): boolean => {
          const pt = ev?.points?.[0]
          if (!pt) return false
          gd.__lastPointClickAt = Date.now()
          const row = rowFromPoint(dataRef.current, layoutRef.current, pt)
          // Quick filter mode: filter on the first click and cancel the native
          // zoom — in this mode a click means "filter by this", never "zoom in".
          if (quickModeRef.current && row) {
            const src = ev?.event as { ctrlKey?: boolean; metaKey?: boolean } | undefined
            fireQuickFilterRef.current(
              row, Boolean(src?.ctrlKey || src?.metaKey || additiveRef.current),
            )
            return false
          }
          const key = pointKey(pt)
          // Second click on the same node while a first is pending → double →
          // underlying (only meaningful for a resolvable leaf row).
          if (clickTimerRef.current && clickKeyRef.current === key) {
            clearTimeout(clickTimerRef.current)
            clickTimerRef.current = null
            clickKeyRef.current = ''
            if (row) runDoubleClick(row)
            return false // cancel the native zoom on this (second) click too
          }
          // First click: defer, then — if no second click follows — replicate
          // the native zoom (make the clicked node the new root) and fire
          // onSelect for a leaf drill-through.
          if (clickTimerRef.current) clearTimeout(clickTimerRef.current)
          clickKeyRef.current = key
          clickTimerRef.current = setTimeout(() => {
            clickTimerRef.current = null
            clickKeyRef.current = ''
            try {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const P = (window as any).Plotly
              if (P?.restyle && pt.id != null) {
                void P.restyle(gd, 'level', pt.id, [pt.curveNumber ?? 0])
              }
            } catch { /* zoom is best-effort */ }
            if (row) runSingleClick(row, columnsFromPoint(dataRef.current, layoutRef.current, pt))
          }, 260)
          return false // always cancel the native zoom; we drive it ourselves
        }
        try { gd.removeAllListeners?.('plotly_treemapclick') } catch { /* ignore */ }
        try { gd.removeAllListeners?.('plotly_sunburstclick') } catch { /* ignore */ }
        try { gd.removeAllListeners?.('plotly_iciclclick') } catch { /* ignore */ }
        gd.on('plotly_treemapclick', onNativeDrillClick)
        gd.on('plotly_sunburstclick', onNativeDrillClick)
        gd.on('plotly_iciclclick', onNativeDrillClick)
        return true
      }
      if (!tryAttach()) setTimeout(tryAttach, 60)
    }

    async function build() {
      try {
        const globalName = typeDef!.global
        if (!globalName) return  // native renderer — handled outside build()
        await ensurePlotly()
        await ensureVendor(typeDef!.deps ?? [])
        if (cancelled || !hostRef.current) return

        const Ctor = (window as unknown as Record<string, ChartConstructor>)[globalName]
        if (typeof Ctor !== 'function') {
          throw new Error(`Chart class "${globalName}" failed to load`)
        }

        // Update in place if we already have a live instance of this type
        // whose host is still on the page.
        if (instanceRef.current && (instanceGlobalRef.current !== globalName
          || !hostRef.current.isConnected || !hostRef.current.querySelector('.js-plotly-plot, div'))) {
          dropInstance()
        }
        if (instanceRef.current) {
          const inst = instanceRef.current as unknown as {
            _chromeHost?: HTMLElement | null
            _onShowUnderlying?: (filters: Record<string, unknown>) => void
            _onOpenFilter?: () => void
            _hostFilterActive?: boolean
          }
          inst._chromeHost = chromeHostRef.current
          inst._onShowUnderlying = onShowUnderlying ? (f) => showUnderlyingRef.current(f || {}) : undefined
          inst._onOpenFilter = filterMenuEnabled ? () => openFilterRef.current() : undefined
          inst._hostFilterActive = filterActive
          ;(inst as { _eventHooks?: MountHooks })._eventHooks = eventHooksRef.current
          instanceRef.current.update(renderData, chartOptions, chartLayout)
          // Chart render() implementations recreate the Plotly div (purge +
          // newPlot), which drops the click handler bound to the old div — so
          // re-attach after every update, else drill / drill-through / underlying
          // clicks die after the first data change or auto-refresh.
          attachPlotlyClick()
          return
        }

        const selector = `#${idRef.current}`
        const instance = new Ctor(selector, renderData, chartOptions, chartLayout)
        // Point the chart's chrome (the ⋮ menu) at the host header slot BEFORE
        // the first render, so the button lands in the header, not over the plot.
        // Also wire the "View underlying data" menu item (whole-result variant).
        const newInst = instance as unknown as {
          _chromeHost?: HTMLElement | null
          _onShowUnderlying?: (filters: Record<string, unknown>) => void
          _onOpenFilter?: () => void
          _hostFilterActive?: boolean
        }
        newInst._chromeHost = chromeHostRef.current
        newInst._onShowUnderlying = onShowUnderlying ? (f) => showUnderlyingRef.current(f || {}) : undefined
        newInst._onOpenFilter = filterMenuEnabled ? () => openFilterRef.current() : undefined
        newInst._hostFilterActive = filterActive
        ;(newInst as { _eventHooks?: MountHooks })._eventHooks = eventHooksRef.current
        // ADV_FORMAT colours find the chart behind a plot through its host.
        registerChartHost(hostRef.current, instance)
        instance.render()
        instanceRef.current = instance
        instanceGlobalRef.current = globalName
        attachPlotlyClick()
      } catch (e) {
        if (cancelled) return
        const err = e instanceof Error ? e : new Error(String(e))
        setError(err.message)
        onError?.(err)
      }
    }

    void build()
    return () => {
      cancelled = true
    }
    // renderData/renderLayout drive a re-render when the drill level changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // `chartOptions` carries `dark`: a theme switch redraws the chart, and
    // every draw passes through the Plotly theme (plotlyTheme.ts).
  }, [renderData, renderLayout, chartLayout, chartOptions, typeDef?.global])

  // The header slot may only exist after the async build() resolves (host sets
  // it via a callback ref). When it (re)appears, hand it to the live instance
  // and redraw the chrome so the ⋮ moves into the header.
  useEffect(() => {
    const inst = instanceRef.current as unknown as {
      _chromeHost?: HTMLElement | null
      _onShowUnderlying?: (filters: Record<string, unknown>) => void
      _onOpenFilter?: () => void
      _hostFilterActive?: boolean
      _renderChartChrome?: () => void
    } | null
    if (!inst) return
    inst._chromeHost = effectiveChromeHost
    // Keep the underlying-data menu wiring in sync (host may toggle it on/off).
    inst._onShowUnderlying = onShowUnderlying ? (f) => showUnderlyingRef.current(f || {}) : undefined
    // Same for the filter hook + its active dot, so entering/leaving a filter
    // redraws the funnel indicator without rebuilding the chart.
    inst._onOpenFilter = filterMenuEnabled ? () => openFilterRef.current() : undefined
    inst._hostFilterActive = filterActive
    try { inst._renderChartChrome?.() } catch { /* ignore chrome redraw errors */ }
  }, [effectiveChromeHost, onShowUnderlying, filterMenuEnabled, filterActive])

  // Tear the chart down on unmount (separate effect so data changes don't
  // destroy+recreate — those go through .update above).
  useEffect(() => {
    return () => {
      // Cancel any pending single-click timer so it can't setState post-unmount.
      if (clickTimerRef.current) { clearTimeout(clickTimerRef.current); clickTimerRef.current = null }
      try {
        instanceRef.current?.destroy()
      } catch {
        /* ignore teardown errors */
      }
      // Clear any ⋮ button we planted in the host header slot (it lives outside
      // our own container, so the chart's teardown won't reach it).
      try {
        chromeHostRef.current?.querySelectorAll('.bv-chrome').forEach((n) => n.remove())
      } catch { /* ignore */ }
      instanceRef.current = null
      instanceGlobalRef.current = null
    }
  }, [])

  // Keep the chart sized to its container.
  useEffect(() => {
    const host = hostRef.current
    if (!host || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => resizeChart(host))
    ro.observe(host)
    return () => ro.disconnect()
  }, [])

  // The in-visual filter builder. Portaled to <body> so a tile's overflow can't
  // clip it, which also means ONE element can be dropped into every return
  // branch below without affecting layout.
  const filterUi = filterOpen
    ? createPortal(
        <VisualFilterDialog
          open
          onClose={() => setFilterOpen(false)}
          // Built over the UNFILTERED dataset so the value picklists still offer
          // every value — otherwise the current filter would hide the values you
          // need to widen it to.
          jsonData={jsonData}
          visualLayout={visualLayout}
          value={ownFilter}
          onChange={setOwnFilter}
          title={`Filter ${String(visualLayout?.VisualName || visualLayout?.ID || 'this visual')}`}
        />,
        document.body,
      )
    : null

  const notesUi = notesOpen && notesLayer ? (
    <NotesPanel
      anchor={notesOpen.rect}
      layer={notesLayer}
      notes={activeNotes?.items ?? notesLayer.items ?? []}
      canAdd={Boolean(activeNotes?.canAdd ?? notesLayer.can_add) && Boolean(notesTransport)}
      admin={activeNotes?.admin}
      project={eventsRaw?.ref?.project}
      objName={eventsRaw?.ref?.obj_name}
      transport={notesTransport}
      initialDate={notesOpen.date}
      onNotes={(items, canAdd, admin) => setNotesOverride({ key: eventsRaw, items, canAdd, admin })}
      onClose={() => setNotesOpen(null)}
    />
  ) : null
  const overlaysUi = <>{filterUi}{notesUi}</>

  // "Showing the first N rows" — built once and placed on EVERY render path,
  // because a partial result is partial whichever renderer draws it. The two
  // variants differ only in how they sit: inline in a header strip that
  // already exists, sticky at the top of a scroller that has none.
  const rowLimitInline = (
    <RowLimitBadge
      truncated={truncated} rowLimit={rowLimit} rowCeiling={rowCeiling}
      rowLimitAuthored={rowLimitAuthored} rowLimitFixed={rowLimitFixed}
      onRowLimit={onRowLimit} variant="inline"
    />
  )
  const rowLimitSticky = (
    <RowLimitBadge
      truncated={truncated} rowLimit={rowLimit} rowCeiling={rowCeiling}
      rowLimitAuthored={rowLimitAuthored} rowLimitFixed={rowLimitFixed}
      onRowLimit={onRowLimit} variant="sticky"
    />
  )

  // While the mode is armed the whole visual reads as clickable — the crosshair
  // is the per-visual half of the mode's affordance (the host paints the tile
  // outline + the banner).
  const quickCursor: CSSProperties =
    quickFilterMode && onQuickFilter ? { cursor: 'crosshair' } : {}

  if (error) {
    return (
      <div
        className={className}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16, fontSize: 13, textAlign: 'center',
          color: 'var(--pg-error, var(--color-error, #dc2626))',
          border: '1px dashed var(--pg-border, var(--color-border, #e5e7eb))', borderRadius: 12,
          minHeight: 120, ...style,
        }}
      >
        {error}
      </div>
    )
  }

  // Native React renderer (table / pivot / cards / feeds / html / calendar /
  // tree / gantt / combo / map). These produce real DOM with a natural content
  // height — in autoHeight mode let that height stand (a card stays short);
  // otherwise fill the container as before.
  if (typeDef?.native) {
    const Renderer = NATIVE[typeDef.native]
    // ⋮ menu for native renderers (Export CSV / Fullscreen) when the toolbar
    // option is on. Docked into the host header slot (portal) when one is given
    // (dashboard tile / preview header); otherwise a slim internal header strip
    // in menuHeader mode; otherwise no menu (legacy — natives never floated one).
    const nativeMenuEnabled = optionsEnableMenu(options)
    const menuFileName = String(visualLayout?.VisualName || visualLayout?.ID || 'visual')
    const nativeMenu = nativeMenuEnabled ? (
      <NativeVisualMenu
        jsonData={filteredData}
        visualLayout={visualLayout}
        fileName={menuFileName}
        getFullscreenTarget={() => nativeWrapRef.current}
        onShowUnderlying={onShowUnderlying ? () => showUnderlyingRef.current({}) : undefined}
        // Same filter mechanism the charts' ⋮ "Filter" now routes to.
        onOpenFilter={filterMenuEnabled ? () => setFilterOpen(true) : undefined}
        filterActive={filterActive}
      />
    ) : null

    // Native renderers (table / cards / pivot / feeds / …) report a clicked ROW
    // through onSelect. In quick filter mode that row becomes the filter picks
    // instead of a drill-through — one wrap here covers every native renderer,
    // so none of them needs to know the mode exists.
    const nativeSelect: SelectHandler | undefined =
      quickFilterMode && onQuickFilter
        ? (row) => { fireQuickFilterRef.current(row) }
        : onSelect

    const renderer = (
      <Renderer
        jsonData={filteredData}
        visualLayout={visualLayout}
        optionsArr={options}
        onSelect={nativeSelect}
        // A wizard's pick_row (table only); never in quick filter mode, where
        // a click is a filter pick.
        onRowClick={quickFilterMode ? undefined : onRowClick}
        // The sizing mode, not just the styles: a renderer that hands its box
        // to a measuring library (ProPivot, for grid + pivot) cannot tell an
        // auto-height host from a fixed one by reading the DOM — `minHeight`
        // alone makes the box look sized — and paints nothing when it guesses.
        autoHeight={autoHeight}
        dark={dark}
        eventHooks={eventHooksRef.current}
      />
    )

    // `minHeight: 240` applies ONLY in autoHeight mode, where nothing else gives
    // the visual a height. Inside a fixed-height host (a dashboard tile) the body
    // must FIT that host and scroll internally — a floor there made the body
    // taller than the tile, and the tile's `overflow: hidden` sliced the bottom
    // of the visual (a card's last band, a chart's axis) clean off.
    // Slot into a host-provided header (outside our container) — no layout change.
    if (nativeMenu && chromeHost) {
      const bodyStyle: CSSProperties = autoHeight
        ? { width: '100%', maxHeight: DEFAULT_MAX_HEIGHT, overflow: 'auto', ...quickCursor, ...style }
        : { width: '100%', height: '100%', minHeight: 0, overflow: 'auto', ...quickCursor, ...style }
      return (
        <div ref={nativeWrapRef} className={className} style={bodyStyle} data-viz-dark={dark ? '' : undefined}>
          {createPortal(nativeMenu, chromeHost)}
          {rowLimitSticky}
          {renderer}
          {overlaysUi}
        </div>
      )
    }

    // menuHeader mode: own a slim header strip above the renderer.
    if (nativeMenu && menuHeader) {
      const innerStyle: CSSProperties = autoHeight
        ? { maxHeight: DEFAULT_MAX_HEIGHT, overflow: 'auto' }
        : { flex: 1, minHeight: 0, overflow: 'auto' }
      const outerStyle: CSSProperties = autoHeight
        ? { width: '100%', display: 'flex', flexDirection: 'column', ...quickCursor, ...style }
        : { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', ...quickCursor, ...style }
      return (
        <div ref={nativeWrapRef} className={className} style={outerStyle} data-viz-dark={dark ? '' : undefined}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, minHeight: 28, flexShrink: 0 }}>
            {rowLimitInline}
            {nativeMenu}
          </div>
          <div style={innerStyle}>{renderer}</div>
          {overlaysUi}
        </div>
      )
    }

    const nativeStyle: CSSProperties = autoHeight
      ? { width: '100%', maxHeight: DEFAULT_MAX_HEIGHT, overflow: 'auto', ...quickCursor, ...style }
      : { width: '100%', height: '100%', minHeight: 0, overflow: 'auto', ...quickCursor, ...style }
    return (
      <div ref={nativeWrapRef} className={className} style={nativeStyle} data-viz-dark={dark ? '' : undefined}>
        {rowLimitSticky}
        {renderer}
        {overlaysUi}
      </div>
    )
  }

  // Vendored chart (Plotly). The drawing surface needs an explicit height; in
  // autoHeight mode give it a sensible default (a caller-supplied `style.height`
  // still wins), otherwise fill the container.
  const chartStyle: CSSProperties = autoHeight
    ? { width: '100%', height: DEFAULT_CHART_HEIGHT, ...quickCursor, ...style }
    // Same as the native path: no floor inside a fixed-height host, or the chart
    // spills past a short tile and is clipped rather than drawn smaller.
    : { width: '100%', height: '100%', minHeight: 0, ...quickCursor, ...style }

  // A chart ALWAYS renders as a flex column: an optional header strip (drill
  // breadcrumb on the left, the ⋮ slot on the right in menuHeader mode) above a
  // stable host div. Keeping one structure — the header strip toggles content,
  // it isn't added/removed — means the host div (and the live chart instance
  // bound to its id) is never recreated when we start/stop drilling.
  const bc = drilled && drillState ? drillBreadcrumb(drillState) : null
  // An active filter is stated in the header, with a one-click way out of the
  // part the user set here — a visual must never quietly show a subset.
  const filterChip = filterActive ? (
    <span
      title={
        (modelBacked
          ? 'Filtered — the visual was recomputed from the shared dataset'
          : `Showing ${filterApp.rowsAfter} of ${filterApp.rowsBefore} loaded rows`
            + (filterApp.regrouped ? ' (measures re-aggregated)' : ''))
        + (filterApp.skipped.length
          ? `. Ignored: ${filterApp.skipped.map((sk) => sk.column).join(', ')} (not in this visual's data)`
          : '')
      }
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
        fontSize: 11, lineHeight: 1.6, padding: '0 6px', borderRadius: 10,
        border: '1px solid var(--pg-primary, var(--color-accent, #3b82f6))',
        color: 'var(--pg-primary, var(--color-accent, #3b82f6))', background: 'transparent',
      }}
    >
      <FunnelIcon size={10} />
      {modelBacked ? 'filtered' : `${filterApp.rowsAfter} / ${filterApp.rowsBefore}`}
      {hasFilterRules(ownFilter) && (
        <button
          type="button"
          onClick={() => setOwnFilter(null)}
          title="Clear this visual's filter"
          aria-label="Clear this visual's filter"
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, color: 'inherit', fontSize: 11, lineHeight: 1 }}
        >
          ✕
        </button>
      )}
    </span>
  ) : null
  const showHeaderStrip = useInternalHeader || drilled || filterActive || !!truncated
  return (
    <div className={className} style={{ ...chartStyle, display: 'flex', flexDirection: 'column' }} data-viz-dark={dark ? '' : undefined}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        minHeight: showHeaderStrip ? 28 : 0, flexShrink: 0,
        padding: showHeaderStrip ? '0 2px' : 0,
      }}>
        {bc && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, fontSize: 12, color: 'var(--pg-text-secondary, var(--color-text-secondary, #6b7280))' }}>
            <button
              type="button"
              onClick={drillUp}
              title="Back one level"
              style={{
                border: '1px solid var(--pg-border, var(--color-border, #d6dae0))', borderRadius: 6,
                background: 'transparent', color: 'inherit', cursor: 'pointer',
                padding: '1px 8px', fontSize: 12, flexShrink: 0,
              }}
            >
              ← Back
            </button>
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {bc.crumbs.join('  ▸  ')}
              {bc.currentDimLabel ? `  ▸  ${bc.currentDimLabel}` : ''}
            </span>
          </div>
        )}
        <div style={{ flex: 1 }} />
        {rowLimitInline}
        {filterChip}
        {useInternalHeader && (
          <span ref={setInternalSlot} style={{ display: 'inline-flex', alignItems: 'center' }} />
        )}
      </div>
      <div ref={hostRef} id={idRef.current} style={{ flex: 1, minHeight: 0, width: '100%' }} />
      {overlaysUi}
    </div>
  )
}

export default InsightVisual
